import { useState, type ElementType, type FormEvent } from 'react';
import { ActionIcon, Avatar, Box, Button, Group, Input, Menu } from '@mantine/core';
import {
  IconBookmark,
  IconBoxSeam,
  IconChevronDown,
  IconClipboardList,
  IconCompass,
  IconHome,
  IconLogout,
  IconMoon,
  IconScan,
  IconSearch,
  IconSettings,
  IconStar,
  IconSun,
  IconTerminal2,
  IconUser,
} from '@tabler/icons-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '@/contexts/user-context';
import { API_BASE_URL } from '@/api/api';
import { useBetaTheme } from '../theme/use-beta-theme';
import { LogoMark } from '../components/logo';

interface NavItem {
  to: string;
  label: string;
  icon: ElementType;
  requiresAuth?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/expansions', label: 'Expansions', icon: IconCompass },
  { to: '/search', label: 'Search', icon: IconSearch },
  { to: '/collections', label: 'Collection', icon: IconBoxSeam },
  { to: '/bindings', label: 'Binders', icon: IconBookmark, requiresAuth: true },
  { to: '/for-you', label: 'For You', icon: IconHome, requiresAuth: true },
];

export function BetaHeader() {
  const { user, error } = useUser();
  const { theme, toggle } = useBetaTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState('');
  const authed = Boolean(user) && !error;

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
  };

  const handleLogout = () => {
    window.location.href = `${API_BASE_URL}/user/logout`;
  };

  const userMenu = authed ? (
    <Menu shadow="sm" width={200} position="bottom-end">
      <Menu.Target>
        <Button
          variant="subtle"
          size="sm"
          radius="md"
          rightSection={<IconChevronDown size={14} />}
          leftSection={
            user?.profilePicture ? (
              <Avatar src={user.profilePicture} size={24} radius="md" alt={user.name || 'Account'} />
            ) : (
              <Avatar size={24} radius="md" color="accent">
                {user?.name ? user.name.charAt(0).toUpperCase() : <IconUser size={16} />}
              </Avatar>
            )
          }
        >
          <span className="beta-account-label">{user?.name || 'Account'}</span>
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>{user?.name || 'Account'}</Menu.Label>
        <Menu.Item leftSection={<IconHome size={15} />} component={Link} to="/for-you">
          For You
        </Menu.Item>
        <Menu.Item leftSection={<IconSettings size={15} />} component={Link} to="/account">
          Account
        </Menu.Item>
        <Menu.Item leftSection={<IconStar size={15} />} component={Link} to="/wants">
          Want list
        </Menu.Item>
        <Menu.Item leftSection={<IconClipboardList size={15} />} component={Link} to="/bulk-entry">
          Bulk entry
        </Menu.Item>
        <Menu.Item leftSection={<IconScan size={15} />} component={Link} to="/scanner">
          Scan card
        </Menu.Item>
        <Menu.Item leftSection={<IconTerminal2 size={15} />} component={Link} to="/mcp">
          MCP server
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item leftSection={<IconLogout size={15} />} onClick={handleLogout} color="red">
          Log out
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  ) : (
    <Button component={Link} to="/login" variant="filled" size="sm" radius="md">
      Sign in
    </Button>
  );

  return (
    <Box component="header" className="beta-header">
      <div className="beta-header-inner">
        <Link to="/" className="beta-wordmark" aria-label="PokeBinder — home">
          <LogoMark size={24} />
          <span>PokeBinder</span>
        </Link>

        <nav className="beta-nav" aria-label="Primary">
          {NAV_ITEMS.filter((item) => !item.requiresAuth || authed).map((item) => {
            const active =
              location.pathname === item.to ||
              (item.to !== '/' && location.pathname.startsWith(item.to));
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`beta-nav-link${active ? ' active' : ''}`}
                aria-current={active ? 'page' : undefined}
              >
                <item.icon size={17} stroke={1.75} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <form className="beta-search-wrap" role="search" onSubmit={submitSearch}>
          <Input
            size="sm"
            aria-label="Search cards and sets"
            placeholder="Search cards and sets…"
            leftSection={<IconSearch size={16} color="var(--beta-text-3)" />}
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
          />
        </form>

        <Group className="beta-header-right" gap={10}>
          <ActionIcon
            component={Link}
            to="/search"
            className="beta-mobile-search-btn"
            variant="subtle"
            size="lg"
            radius="md"
            aria-label="Search"
          >
            <IconSearch size={19} />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            size="lg"
            radius="md"
            onClick={toggle}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
          </ActionIcon>
          {userMenu}
        </Group>
      </div>
    </Box>
  );
}
